import { useContext, useEffect, useRef } from 'react';
import { useLocation, useSearchParams, useMatches, UNSAFE_NavigationContext as NavigationContext } from 'react-router';
import { normalizePath } from '@helsenorge/core-utils/url';
import { track } from '@helsenorge/framework-utils/amplitude';
import { PageView } from '@helsenorge/framework-utils/amplitude/ampli';
import globalVariablesPlugin from '@helsenorge/framework-utils/amplitude/global-variables-plugin';
import { getPath } from '@helsenorge/framework-utils/hn-page';
/**
 * Bygg en safe path ved å erstatte paramverdier med :placeholder-navn.
 * Sorterer etter lengde (lengste først) for å unngå delvise erstatninger.
 *
 * Konverterer pathname "/bruker/123" og params { id: "123" } til "/bruker/:id".
 */
const buildSafePath = (pathname, params) => {
    let safePath = pathname;
    const entries = Object.entries(params)
        .filter((entry) => !!entry[1])
        .sort((a, b) => b[1].length - a[1].length);
    for (const [key, value] of entries) {
        safePath = safePath.replace(value, `:${key}`);
    }
    return safePath;
};
/**
 * Spor sidevisninger
 */
const useTrackPageView = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const matches = useMatches();
    const navContext = useContext(NavigationContext);
    const basename = navContext?.basename;
    const previousState = useRef(null);
    const currentMatch = matches[matches.length - 1];
    const matchId = currentMatch?.id;
    useEffect(() => {
        const handle = currentMatch?.handle;
        // 1. Finn path som er safe for tracking
        let relativePath;
        const customPathConfig = handle?.analytics?.path;
        if (typeof customPathConfig === 'string') {
            relativePath = customPathConfig;
        }
        else if (typeof customPathConfig === 'function') {
            relativePath = customPathConfig(location.pathname);
        }
        else {
            relativePath =
                buildSafePath(currentMatch?.pathname ?? location.pathname, currentMatch?.params ?? {}) ?? '/' + getPath() + '/ukjent-path';
        }
        // 2. Slå sammen vertikalens basename med path
        const fullReportedPath = normalizePath(basename, relativePath);
        // 3. Finn den delen av query string som er safe for tracking
        const defaultWhitelistedKeys = ['utm_campaign', 'utm_content', 'utm_medium', 'utm_source', 'utm_term'];
        const customWhitelistedKeys = handle?.analytics?.searchParams ?? [];
        const whitelistedKeys = [...defaultWhitelistedKeys, ...customWhitelistedKeys];
        const safeQueryParams = [];
        if (whitelistedKeys.length > 0) {
            whitelistedKeys.forEach(key => {
                const value = searchParams.get(key);
                if (value !== null) {
                    safeQueryParams.push(`${key}=${value}`);
                }
            });
        }
        safeQueryParams.sort((a, b) => a.localeCompare(b));
        const safeQuery = safeQueryParams.join('&');
        // 4. Overvåk endringer i URL, alle endringer i URL telles som en ny sidevisning (ikke bare endring i den "safe" delen)
        const prev = previousState.current;
        const pathChanged = !prev || prev.pathname !== location.pathname;
        const searchChanged = !prev || prev.search !== location.search;
        if (pathChanged || searchChanged) {
            // 5. Oppdater verdier i global plugin slik at andre eventer også bruker samme properties
            globalVariablesPlugin.setPagePath(fullReportedPath);
            globalVariablesPlugin.setQuery(safeQuery);
            // 6. Spor sidevisning
            track(new PageView());
            // 7. Ta vare på verdier for å sammenlikne ved neste sidevisning
            previousState.current = {
                pathname: location.pathname,
                search: location.search,
            };
        }
    }, [location.pathname, searchParams, matchId, basename]);
};
export default useTrackPageView;
